import React, { useEffect, useState, useRef } from "react";
import { LogOut, Menu, Search, Settings, User2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import axios from "axios";

import userProfile from "assets/images/users/user-profile.png";

import LightDark from "Common/LightDark";
import NotificationDropdown from "Common/NotificationDropdown";
import { changeLeftsidebarSizeType } from "slices/thunk";
import { toast } from "react-toastify";
import { getUsername } from "Common/Components/getRoleId";
import {
  UserRole,
  ROLE_LABELS,
  getCurrentUserRole,
  setCurrentUserRole,
} from "Common/constants/permissions";

const BASE_URL = "https://portal-api.mahanpanel.com";

// ============================================================
// 🔍 نگاشت کامل عبارات فارسی به مسیرهای انگلیسی
// ============================================================
const pageTranslations: Record<string, string> = {
  // ProvincialRegistration
  "ثبت استانی": "/ProvincialRegistration",
  "ثبت استان": "/ProvincialRegistration",
  استان: "/ProvincialRegistration",

  // CityRegiste
  "ثبت شهر": "/CityRegiste",
  شهر: "/CityRegiste",

  // Measurementpage
  "واحد اندازه گیری": "/Measurementpage",
  "اندازه گیری": "/Measurementpage",

  // WarehouseRegistration
  "ثبت انبار": "/WarehouseRegistration",
  انبار: "/WarehouseRegistration",

  // GoodsTypeRegistration
  "ثبت نوع کالا": "/GoodsTypeRegistration",
  "نوع کالا": "/GoodsTypeRegistration",

  // GoodsAndServicesRegistration
  "ثبت کالا و خدمات": "/GoodsAndServicesRegistration",
  "کالا و خدمات": "/GoodsAndServicesRegistration",

  // CustomerRegistration
  "ثبت مشتری": "/CustomerRegistration",
  مشتری: "/CustomerRegistration",

  // MinimumPaymentSettings
  "حداقل پرداخت": "/MinimumPaymentSettings",
  "تنظیمات حداقل پرداخت": "/MinimumPaymentSettings",

  // ProductRegistration
  "ثبت محصول": "/ProductRegistration",
  محصول: "/ProductRegistration",

  // ProductPriceRegistration
  "ثبت قیمت محصول": "/ProductPriceRegistration",
  "قیمت محصول": "/ProductPriceRegistration",

  // manageUser
  "مدیریت کاربران": "/manageUser",
  کاربران: "/manageUser",

  // ProformaInvoices
  "پیش فاکتور": "/ProformaInvoices",
  فاکتور: "/ProformaInvoices",

  // SalesOrders
  "سفارشات فروش": "/SalesOrders",
  "سفارشهای فروش": "/SalesOrders",
  سفارش: "/SalesOrders",

  // ReadyToProduceGoods
  "آماده تولید": "/ReadyToProduceGoods",
  تولید: "/ReadyToProduceGoods",

  // GoodsInProduction
  "کالاهای در حال تولید": "/GoodsInProduction",
  "در حال تولید": "/GoodsInProduction",

  // ProducedGoods
  "کالاهای تولید شده": "/ProducedGoods",
  "تولید شده": "/ProducedGoods",

  // ProductsWarehouse
  "انبار محصولات": "/ProductsWarehouse",
  "محصولات انبار": "/ProductsWarehouse",

  // OrdersInProduction
  "سفارشات در تولید": "/OrdersInProduction",
  "سفارشهای در تولید": "/OrdersInProduction",
  "سفارشات تولید": "/OrdersInProduction",
  "سفارشهای تولید": "/OrdersInProduction",

  // ProducedOrders
  "سفارشات تولید شده": "/ProducedOrders",
  "سفارشهای تولید شده": "/ProducedOrders",
  "تولید سفارش": "/ProducedOrders",

  // OrdersInWarehouse
  "سفارشات در انبار": "/OrdersInWarehouse",
  "سفارشهای در انبار": "/OrdersInWarehouse",   // ✅ اضافه شد
  "سفارشهای انبار": "/OrdersInWarehouse",       // ✅ اضافه شد
  "انبار سفارشات": "/OrdersInWarehouse",
  "در انبار": "/OrdersInWarehouse",             // ✅ اضافه شد

  // OrdersExitedWarehouse
  "سفارشات خروج از انبار": "/OrdersExitedWarehouse",
  "سفارشهای خروج از انبار": "/OrdersExitedWarehouse",
  "خروج انبار": "/OrdersExitedWarehouse",
  "خروج از انبار": "/OrdersExitedWarehouse",

  // LoadedOrders
  "سفارشات بارگیری": "/LoadedOrders",
  "سفارشهای بارگیری": "/LoadedOrders",
  بارگیری: "/LoadedOrders",

  // DeliveredOrders
  "سفارشات تحویل شده": "/DeliveredOrders",
  "سفارشهای تحویل شده": "/DeliveredOrders",
  تحویل: "/DeliveredOrders",

  // FoamRollWarehouse
  "انبار رول فوم": "/FoamRollWarehouse",
  فوم: "/FoamRollWarehouse",

  // PaymentTypeRegistration
  "ثبت نوع پرداخت": "/PaymentTypeRegistration",
  "نوع پرداخت": "/PaymentTypeRegistration",

  // CurrencyRegistration
  "ثبت ارز": "/CurrencyRegistration",
  // ProformaDescriptions
  "ثبت توضیحات پیش‌فاکتور": "/ProformaDescriptions",
  "توضیحات پیش‌فاکتور": "/ProformaDescriptions",
  "توضیحات": "/ProformaDescriptions",
  ارز: "/CurrencyRegistration",

  // navigation-tabs
  "تب ها": "/navigation-tabs",
  ناوبری: "/navigation-tabs",

  // pages-account-settings
  "تنظیمات حساب": "/pages-account-settings",
  تنظیمات: "/pages-account-settings",

  // user-profile
  "پروفایل کاربر": "/user-profile",
  پروفایل: "/user-profile",
};

// ============================================================

const topbarColorClasses: Record<string, string> = {
  light: "",
  dark: "!bg-gray-900/90 !text-gray-100 [&_input]:!bg-gray-800/70 [&_input]:!text-gray-100",
  brand: "!bg-custom-700/95 !text-white [&_input]:!bg-custom-800/60 [&_input]:!text-white",
};

const Header = ({ handleToggleDrawer, handleDrawer, toggleMobileMenu, layoutTopbarColorType }: any) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const token = localStorage.getItem("app_at_mahan");
  const [username, setusername] = useState<any>(null);
  const [fullname, setfullname] = useState<any>(null);
  const [role, setRole] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    const fetchMe = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRole(res.data?.role_id ?? null);
        if (res.data?.fullname) setfullname(res.data.fullname);
      } catch (error) {
        console.error("Error fetching current user profile:", error);
      }
    };
    fetchMe();
  }, [token]);

  const roleNames: Record<number, string> = {
    0: "سوپرادمین",
    1: "ادمین",
    2: "کارشناس فروش",
    3: "کارشناس حسابداری",
    4: "مدیر فروش",
  };

  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mockRole, setMockRole] = useState<UserRole>(getCurrentUserRole());

  const handleMockRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value as UserRole;
    setCurrentUserRole(newRole);
    setMockRole(newRole);
    window.location.reload();
  };

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setusername(getUsername(token));
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLayoutState = (state: any) => state.Layout;
  const selectLayoutProperties = createSelector(
    selectLayoutState,
    (layout: any) => ({
      layoutSidebarSizeType: layout.layoutSidebarSizeType,
      layoutType: layout.layoutType,
    })
  );

  // ============================================================
  // 🧠 جستجوی هوشمند با اولویت طولانی‌ترین کلید
  // ============================================================
  const handleSearch = () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const lowerQuery = trimmed.toLowerCase();

    // 1. تطابق دقیق (بدون حساسیت به حروف)
    const exactMatch = Object.keys(pageTranslations).find(
      (key) => key.toLowerCase() === lowerQuery
    );
    if (exactMatch) {
      setQuery("");
      navigate(pageTranslations[exactMatch]);
      return;
    }

    // 2. تمام تطابق‌های جزئی (کلید شامل عبارت یا عبارت شامل کلید)
    const matches = Object.entries(pageTranslations).filter(([key]) => {
      const lowerKey = key.toLowerCase();
      return lowerKey.includes(lowerQuery) || lowerQuery.includes(lowerKey);
    });

    if (matches.length > 0) {
      // مرتب‌سازی بر اساس طول کلید (نزولی) → مشخص‌ترین کلید برنده می‌شود
      matches.sort((a, b) => b[0].length - a[0].length);
      const [, path] = matches[0];
      setQuery("");
      navigate(path);
    } else {
      toast.error("صفحه موردنظر یافت نشد! 404");
    }
  };
  // ============================================================

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const { layoutSidebarSizeType, layoutType } = useSelector(
    selectLayoutProperties
  );

  const handleTopbarHamburgerIcon = () => {
    var windowSize = document.documentElement.clientWidth;

    if (windowSize < 1024) {
      toggleMobileMenu?.();
    } else {
      dispatch(
        changeLeftsidebarSizeType(layoutSidebarSizeType === "sm" ? "lg" : "sm")
      );
    }
  };

  useEffect(() => {
    const handleResizeLayout = () => {
      var windowSize = document.documentElement.clientWidth;
      if (windowSize < 768) {
        dispatch(changeLeftsidebarSizeType("lg"));
      } else if (windowSize <= 1024) {
        if (layoutType === "vertical") {
          dispatch(changeLeftsidebarSizeType("sm"));
        } else {
          dispatch(changeLeftsidebarSizeType("lg"));
        }
      } else {
        dispatch(changeLeftsidebarSizeType("lg"));
      }
    };

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResizeLayout);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResizeLayout);
    };
  }, [layoutType, dispatch]);

  return (
    <React.Fragment>
      <header
        id="page-topbar"
        className={`
          fixed right-0 left-0 top-0 z-[1000] transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${scrolled
            ? "bg-sky-50/90 dark:bg-[#1C2E45] backdrop-blur-2xl shadow-[0_8px_32px_-8px_rgba(56,120,200,0.25)] dark:shadow-black/20 border-b border-sky-100/80 dark:border-gray-700/30"
            : "bg-sky-50/70 dark:bg-[#1C2E45] backdrop-blur-xl border-b border-sky-100/80 dark:border-gray-700/20"
          }
          ltr:md:left-vertical-menu rtl:md:right-vertical-menu
          group-data-[sidebar-size=md]:ltr:md:left-vertical-menu-md group-data-[sidebar-size=md]:rtl:md:right-vertical-menu-md
          group-data-[sidebar-size=sm]:ltr:md:left-vertical-menu-sm group-data-[sidebar-size=sm]:rtl:md:right-vertical-menu-sm
          group-data-[layout=horizontal]:ltr:left-0 group-data-[layout=horizontal]:rtl:right-0
          print:hidden group-data-[navbar=hidden]:hidden group-data-[navbar=scroll]:absolute
          group/topbar group-data-[layout=horizontal]:z-[1004]
          ${topbarColorClasses[layoutTopbarColorType] || ""}
        `}
      >
        <div className="layout-width">
          <div className="flex items-center px-4 mx-auto h-[72px] shadow-none border-0">
            <div className="flex items-center w-full group-data-[layout=horizontal]:mx-auto group-data-[layout=horizontal]:max-w-screen-2xl navbar-header gap-4">
              {/* ===== دکمه همبرگر ===== */}
              <button
                onClick={handleTopbarHamburgerIcon}
                type="button"
                className="hidden lg:inline-flex items-center justify-center w-10 h-10 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-sky-500/10 dark:hover:bg-white/5 hover:text-red-600 dark:hover:text-red-400 transition-all duration-300"
              >
                <Menu className="w-5 h-5" strokeWidth={1.8} />
              </button>

              {/* ===== سرچ بار ===== */}
              <div className="relative hidden md:block group-data-[layout=horizontal]:hidden group-data-[layout=horizontal]:lg:block flex-1 max-w-md">
                <div className="relative group/search">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within/search:text-red-500 transition-all duration-300 group-hover/search:scale-110"
                    strokeWidth={2}
                  />
                  <input
                    type="text"
                    className="w-full py-3 pr-12 pl-12 text-sm rounded-2xl border border-sky-100 dark:border-gray-700/40 bg-white/70 dark:bg-gray-800/60 backdrop-blur-md shadow-[0_2px_10px_rgba(0,0,0,0.04)] focus:border-red-200 dark:focus:border-red-800/50 focus:shadow-[0_0_0_4px_rgba(220,38,38,0.12),0_4px_20px_rgba(0,0,0,0.06)] dark:focus:shadow-[0_0_0_4px_rgba(220,38,38,0.18),0_4px_20px_rgba(0,0,0,0.3)] transition-all duration-300 outline-none text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 placeholder:font-light"
                    placeholder="دنبال چه صفحه‌ای هستی؟"
                    autoComplete="off"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  {query && (
                    <button
                      onClick={() => setQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full bg-gray-200/70 dark:bg-gray-700/70 backdrop-blur-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 hover:scale-110 active:scale-95"
                    >
                      <span className="text-xs font-bold">✕</span>
                    </button>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500/0 via-red-500/0 to-rose-500/0 group-focus-within/search:from-red-500/60 group-focus-within/search:via-red-500/80 group-focus-within/search:to-rose-500/60 transition-all duration-500 rounded-b-2xl"></div>
                </div>
              </div>

              {/* ===== ابزارهای سمت راست ===== */}
              <div className="flex gap-1 ms-auto items-center">
                <div className="transform transition-all duration-300 hover:scale-110 hover:rotate-[-4deg]">
                  <LightDark />
                </div>

                <div className="hidden sm:block">
                  <NotificationDropdown />
                </div>

                <div className="relative items-center hidden h-header md:flex">
                  <button
                    onClick={handleToggleDrawer}
                    type="button"
                    className="inline-flex justify-center items-center w-9 h-9 rounded-full text-gray-600 dark:text-gray-300 hover:bg-sky-500/10 dark:hover:bg-white/5 hover:text-red-600 dark:hover:text-red-400 transition-all duration-300"
                  >
                    <Settings className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                </div>

                {/* ===== منوی کاربری با state ساده ===== */}
                <div className="relative flex items-center h-header" ref={profileRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 p-1 rounded-full transition-all duration-300 hover:bg-sky-500/10 dark:hover:bg-white/5 group/profile"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover/profile:text-red-600 dark:group-hover/profile:text-red-400 transition-colors duration-300">
                        {fullname || username || "مدیر سیستم"}
                      </span>
                      <div className="relative transition-all duration-300 group-hover/profile:scale-105 group-hover/profile:shadow-[0_0_20px_rgba(220,38,38,0.20)]">
                        <img
                          src={userProfile}
                          alt="avatar"
                          className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-gray-700 shadow-sm group-hover/profile:border-red-400 dark:group-hover/profile:border-red-500 transition-colors duration-300"
                        />
                        <span className="absolute bottom-0 right-0 block w-2.5 h-2.5 rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-800"></span>
                      </div>
                    </div>
                  </button>

                  {isProfileOpen && (
                    <div
                      className="absolute z-50 p-4 ltr:text-left rtl:text-right bg-white/90 dark:bg-gray-800/90 backdrop-blur-2xl rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.2)] dark:shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] min-w-[18rem] border border-slate-300 dark:border-slate-500"
                      style={{
                        top: '100%',
                        left: 0,
                        marginTop: '8px'
                      }}
                    >
                      <div className="relative mb-4 text-center rounded-xl overflow-hidden bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 p-4 border border-red-100/50 dark:border-red-800/30">
                        <div className="absolute top-0 left-0 w-20 h-20 bg-red-500/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
                        <h6 className="text-sm font-medium text-slate-600 dark:text-gray-300 relative z-10">
                          به ماهان پانل خوش آمدید
                        </h6>
                      </div>

                      <div className="flex gap-4 mb-4 p-3 rounded-xl bg-gray-50/80 dark:bg-gray-700/30 backdrop-blur-sm border border-gray-100 dark:border-gray-700/50">
                        <div className="shrink-0">
                          <img src={userProfile} alt="" className="w-14 h-14 rounded-full shadow-md border-2 border-white dark:border-gray-600" />
                        </div>
                        <div>
                          <h6 className="text-base font-semibold text-gray-800 dark:text-white">
                            {fullname || username || "مدیر سیستم"}
                          </h6>
                          {role && roleNames[role] && (
                            <p className="text-sm text-slate-500 dark:text-gray-400 flex items-center gap-1">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500"></span>
                              {roleNames[role]}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* سلکتور تست نقش (اختیاری) */}
                      {/* <div className="mb-4 p-3 rounded-xl bg-gray-50/80 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50">
                        <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1.5">
                          نقش کاربری (تستی)
                        </label>
                        <select
                          className="form-select w-full text-sm"
                          value={mockRole}
                          onChange={handleMockRoleChange}
                        >
                          {Object.values(UserRole).map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      </div> */}

                      <ul className="space-y-1.5">
                        <li>
                          <Link
                            to="/user-profile"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
                            onClick={() => setIsProfileOpen(false)}
                          >
                            <User2 size={18} /> پروفایل
                          </Link>
                        </li>
                        <li className="pt-2 mt-1 border-t border-gray-100 dark:border-gray-700/50">
                          <Link
                            to="/logout"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                            onClick={() => setIsProfileOpen(false)}
                          >
                            <LogOut size={18} /> خروج از سیستم
                          </Link>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {scrolled && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-red-500/40 to-transparent animate-pulse"></div>
        )}
      </header>
    </React.Fragment>
  );
};

export default Header;
